import { Box, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import Selector from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/Selector'
import { viewTypeOptions } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/viewTypeOptions'
import ViewOptionsSectionButton from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/ShiftActionsBar/ViewOptionsSectionButton'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'
import View from 'modules/shift_management/store/internal/models/View'

interface Props {
  hasNoneShiftManagementPlanning: boolean
}

const NewThemeHeaderViewNavigator = ({ hasNoneShiftManagementPlanning }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const hasMonthlyView = hasFeatureFlag(ShiftManagementFeatureFlags.SHIFT_MANAGEMENT_MONTHLY_VIEW)
  const viewTypeReadonly = !hasMonthlyView || hasNoneShiftManagementPlanning

  const { access: currentUser } = useCurrentSession()
  const [{ shiftsRows }] = useShiftsRows()
  const canManageShifts = shiftsRows.some((shiftsRow) => shiftsRow.canManage)

  const [open, toggle] = useToggle(false)

  const { view, onChangePeriod, onChangeRoute } = useChangeView()

  const onClickBack = () => onChangePeriod(view.previousPeriod)
  const onClickForward = () => onChangePeriod(view.nextPeriod)

  const onClickToday = () => {
    const today = new View({ date: moment(), viewType: view.viewType, groupType: view.groupType })
    onChangePeriod(today)
  }

  const onClickSelect = (date: moment.Moment) => {
    const selectedView = new View({ date, viewType: view.viewType, groupType: view.groupType })
    onChangePeriod(selectedView)
    toggle(false)()
  }

  const onViewChange = (value: string) => {
    onChangeRoute(`${value}/${view.groupType}`)
  }

  const borderStyle = { width: 's1', style: 'solid', color: 'grey400' } as const

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" minWidth="none">
      <Button
        onClick={onClickToday}
        label={i18n.t('common.today')}
        variant="outline"
        disabled={hasNoneShiftManagementPlanning}
      />
      <PopoverMenu type="dialog" onClick={toggle(!open)} open={open}>
        <Box flexDirection="row" minWidth="s200" height="s32">
          <Box
            width="s32"
            height="full"
            alignItems="center"
            justifyContent="center"
            border={{ all: borderStyle }}
            borderRadius={{ topLeft: 'abs008', bottomLeft: 'abs008' }}
            onClick={onClickBack}
            background="grey000White"
          >
            <Icon.Small icon="chevron-left" color="grey600" />
          </Box>

          <PopoverMenu.Trigger disabled={hasNoneShiftManagementPlanning}>
            <Box flexDirection="row" minWidth="s140" height="s32" background="grey000White">
              <Box
                flexDirection="row"
                flexGrow
                paddingX="s8"
                alignItems="center"
                justifyContent="center"
                gap="s4"
                border={{ top: borderStyle, bottom: borderStyle }}
              >
                <Text.Normal
                  weight="normal"
                  color={hasNoneShiftManagementPlanning ? 'grey600' : 'grey800'}
                  ellipsis
                >
                  {view.headerTitle}
                </Text.Normal>
                <Icon.Small icon="chevron-down" color="grey600" />
              </Box>
            </Box>
          </PopoverMenu.Trigger>

          <Box
            width="s32"
            height="full"
            alignItems="center"
            justifyContent="center"
            border={{ all: borderStyle }}
            borderRadius={{ topRight: 'abs008', bottomRight: 'abs008' }}
            onClick={onClickForward}
            background="grey000White"
          >
            <Icon.Small icon="chevron-right" color="grey600" />
          </Box>
        </Box>

        <PopoverMenu.Content side="bottom" align="center" width="s360">
          <DayPicker
            label={i18n.t('common.select')}
            firstDayOfWeek={firstDayOfWeekAsIndex(currentUser.firstDayOfWeek)}
            onChange={(date: string) => onClickSelect(stringToDate(date, 'ui'))}
            minDate={minDate}
            maxDate={maxDate}
            showOnlyCalendar
          />
        </PopoverMenu.Content>
      </PopoverMenu>
      <Selector
        value={view.viewType}
        options={viewTypeOptions(view.groupType)}
        onChange={onViewChange}
        readonly={viewTypeReadonly}
      />
      <ViewOptionsSectionButton
        disabled={hasNoneShiftManagementPlanning}
        canManageShifts={canManageShifts}
      />
    </Box>
  )
}

export default observer(NewThemeHeaderViewNavigator)
